import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation, useBlocker } from "react-router-dom";
import { useQueryClient } from "react-query";
import { useAuthContext } from "../../hooks/useAuthContext";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Card } from "primereact/card";
import { Skeleton } from "primereact/skeleton";
import MeaPage from "../../components/MeaPage";
import MeaLink from "../../components/MeaLink";
import MeaPageTitle from "../../components/MeaPageTitle";
import MeaDetailCard from "../../components/MeaDetailCard";
import MeaDetailField from "../../components/MeaDetailField";
import classesAPI from "../../services/classes";
import scheduleAPI from "../../services/schedule";
import studentsAPI from "../../services/students";
import trainingsAPI from "../../services/trainings";
import accountsAPI from "../../services/accounts";

const resultOptions = [
  { label: "-", value: "ONGOING" },
  { label: "Passage", value: "PASSED" },
  { label: "Redoublement", value: "FAILED" },
];

const resultOptionTemplate = (option) => {
  if (!option) return null;
  let bgClass = "";
  let textClass = "";
  let borderClass = "";
  if (option.value === "PASSED") {
    bgClass = "bg-green-50";
    textClass = "text-green-700";
    borderClass = "border-green-200";
    return (
      <span className={`px-2 py-1 border-round border-1 text-xs font-semibold ${bgClass} ${textClass} ${borderClass}`}>
        {option.label}
      </span>
    );
  } else if (option.value === "FAILED") {
    bgClass = "bg-red-50";
    textClass = "text-red-700";
    borderClass = "border-red-200";
    return (
      <span className={`px-2 py-1 border-round border-1 text-xs font-semibold ${bgClass} ${textClass} ${borderClass}`}>
        {option.label}
      </span>
    );
  } else {
    return <span>{option.label}</span>;
  }
};

const resultValueTemplate = (option, props) => {
  if (!option) {
    return <span>{props.placeholder}</span>;
  }
  if (option.value === "ONGOING") {
    return <span>{option.label}</span>;
  }
  return resultOptionTemplate(option);
};

export function TeacherAvisPassagePage() {
  const { schoolId, trainingId, classId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { auth, accessToken, currentMembership, updateCurrentMembershipPreferences } = useAuthContext();
  const queryClient = useQueryClient();

  const [students, setStudents] = useState([]);
  const [savingStates, setSavingStates] = useState({}); // { [studentId]: 'idle' | 'saving' | 'saved' | 'error' }
  const [originalResults, setOriginalResults] = useState({});
  const [originalAppreciations, setOriginalAppreciations] = useState({});
  const [pendingTrainingId, setPendingTrainingId] = useState(null);

  const activeTrainingId = pendingTrainingId || trainingId;

  const trainingQuery = trainingsAPI.GetOne(schoolId, activeTrainingId, auth?.access_token);
  const training = trainingQuery?.data?.data;

  const trainingsQuery = trainingsAPI.useGetList(
    schoolId,
    auth?.access_token,
    1,
    100,
    ""
  );
  const trainings = trainingsQuery?.data?.data || [];

  const isAdmin = location.pathname.includes("/admin");
  const basePath = isAdmin ? "admin" : "teacher";
  const spaceName = isAdmin ? "Administration" : "Espace Enseignant";

  const totalStudents = students.length;
  const passedCount = students.filter((s) => s.result === "PASSED").length;
  const failedCount = students.filter((s) => s.result === "FAILED").length;
  const ongoingCount = students.filter((s) => !s.result || s.result === "ONGOING").length;

  const passedPercent = totalStudents > 0 ? Math.round((passedCount / totalStudents) * 100) : 0;
  const failedPercent = totalStudents > 0 ? Math.round((failedCount / totalStudents) * 100) : 0;
  const ongoingPercent = totalStudents > 0 ? Math.round((ongoingCount / totalStudents) * 100) : 0;
  const debounceTimersRef = React.useRef({});
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 991);

  useEffect(() => {
    return () => {
      if (debounceTimersRef.current) {
        Object.values(debounceTimersRef.current).forEach((timerId) => clearTimeout(timerId));
      }
      queryClient.removeQueries(["students-enrolled"]);
    };
  }, [queryClient]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 991);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Fetch Class details
  const classeQuery = classesAPI.GetOne(schoolId, activeTrainingId, accessToken, classId);
  const classe = classeQuery?.data?.data;

  // Fetch schedule to list class courses and slots
  const scheduleQuery = scheduleAPI.useGetList(
    schoolId,
    activeTrainingId,
    accessToken,
    1,
    100,
    { classe_id: classId }
  );
  const scheduleItems = scheduleQuery?.data?.data || [];

  // Fetch enrolled students
  const studentsQuery = studentsAPI.useGetEnrolledList(
    schoolId,
    activeTrainingId,
    accessToken,
    1,
    1000,
    { class_id: classId }
  );

  useEffect(() => {
    if (studentsQuery.data?.data) {
      setStudents(studentsQuery.data.data);

      const baselineResults = {};
      const baselineAppreciations = {};
      studentsQuery.data.data.forEach((s) => {
        baselineResults[s.id] = s.result || "ONGOING";
        baselineAppreciations[s.id] = s.result_observation || "";
      });
      setOriginalResults(baselineResults);
      setOriginalAppreciations(baselineAppreciations);
    }
  }, [studentsQuery.data]);

  // Format schedule items into courses & slots string
  const formattedSchedule = React.useMemo(() => {
    if (scheduleItems.length === 0) return "Aucun cours programmé";
    const courseSlots = scheduleItems.map((item) => {
      const courseName = item.course?.name || "";
      const slotName = item.slot?.name || "";
      const hours = item.start_hour && item.end_hour ? ` (${item.start_hour} - ${item.end_hour})` : "";
      
      let teacherName = "";
      const teacher = item.teacher;
      if (teacher?.member?.lastname && teacher?.member?.firstname) {
        teacherName = ` (${teacher.member.lastname.toUpperCase()} ${teacher.member.firstname})`;
      } else if (teacher?.member?.name) {
        teacherName = ` (${teacher.member.name})`;
      }

      return `${courseName} - ${slotName}${hours}${teacherName}`;
    });
    // Remove duplicates
    return [...new Set(courseSlots)].join(" | ");
  }, [scheduleItems]);

  // Track if there are any unsaved modifications compared to the backend data
  const hasUnsavedChanges = React.useMemo(() => {
    const isSaving = Object.values(savingStates).some((status) => status === "saving");
    if (isSaving) return true;

    if (students.length === 0) return false;

    for (const student of students) {
      const currentResult = student.result || "ONGOING";
      const originalResult = originalResults[student.id] || "ONGOING";
      const currentAppreciation = student.result_observation || "";
      const originalAppreciation = originalAppreciations[student.id] || "";

      if (currentResult !== originalResult || currentAppreciation !== originalAppreciation) {
        return true;
      }
    }

    return false;
  }, [students, savingStates, originalResults, originalAppreciations]);

  // Warn on browser tab close/reload
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = "Vous avez des modifications non enregistrées. Voulez-vous vraiment quitter cette page ?";
        return e.returnValue;
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [hasUnsavedChanges]);

  // Warn on router page transition
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      hasUnsavedChanges &&
      currentLocation.pathname !== nextLocation.pathname
  );

  useEffect(() => {
    if (blocker.state === "blocked") {
      const confirmLeave = window.confirm(
        "Vous avez des modifications non enregistrées. Voulez-vous vraiment quitter cette page ?"
      );
      if (confirmLeave) {
        blocker.proceed();
      } else {
        blocker.reset();
      }
    }
  }, [blocker, hasUnsavedChanges]);

  const handleResultChange = async (studentId, newResult) => {
    // Find current student appreciation
    const student = students.find((s) => s.id === studentId);
    const currentAppreciation = student?.result_observation || "";

    // Optimistic update
    setStudents((prev) =>
      prev.map((s) => (s.id === studentId ? { ...s, result: newResult } : s))
    );

    setSavingStates((prev) => ({ ...prev, [studentId]: "saving" }));

    try {
      await studentsAPI.UpdateResult(
        schoolId,
        trainingId,
        accessToken,
        studentId,
        newResult,
        currentAppreciation
      );
      
      // Update baseline result
      setOriginalResults((prev) => ({ ...prev, [studentId]: newResult }));
      queryClient.invalidateQueries(["students-enrolled"]);

      setSavingStates((prev) => ({ ...prev, [studentId]: "saved" }));
      setTimeout(() => {
        setSavingStates((prev) => {
          if (prev[studentId] === "saved") {
            return { ...prev, [studentId]: "idle" };
          }
          return prev;
        });
      }, 2000);
    } catch (error) {
      console.error("Failed to update result:", error);
      setSavingStates((prev) => ({ ...prev, [studentId]: "error" }));
    }
  };

  const handleAppreciationChangeLocal = (studentId, newValue) => {
    setStudents((prev) =>
      prev.map((s) =>
        s.id === studentId ? { ...s, result_observation: newValue } : s
      )
    );

    // Cancel existing debounce timer for this student
    if (debounceTimersRef.current[studentId]) {
      clearTimeout(debounceTimersRef.current[studentId]);
    }

    // Schedule auto-save after 3 seconds of inactivity
    debounceTimersRef.current[studentId] = setTimeout(() => {
      handleAppreciationSave(studentId, newValue);
    }, 3000);
  };

  const handleAppreciationSave = async (studentId, appreciationValue) => {
    // Clear debounce timer
    if (debounceTimersRef.current[studentId]) {
      clearTimeout(debounceTimersRef.current[studentId]);
      delete debounceTimersRef.current[studentId];
    }

    const student = students.find((s) => s.id === studentId);
    const currentResult = student?.result || "ONGOING";

    const originalAppreciation = originalAppreciations[studentId] || "";
    if (appreciationValue === originalAppreciation) {
      return; // No change
    }

    setSavingStates((prev) => ({ ...prev, [studentId]: "saving" }));

    try {
      await studentsAPI.UpdateResult(
        schoolId,
        trainingId,
        accessToken,
        studentId,
        currentResult,
        appreciationValue
      );
      
      // Update baseline appreciation
      setOriginalAppreciations((prev) => ({ ...prev, [studentId]: appreciationValue }));
      queryClient.invalidateQueries(["students-enrolled"]);

      setSavingStates((prev) => ({ ...prev, [studentId]: "saved" }));
      setTimeout(() => {
        setSavingStates((prev) => {
          if (prev[studentId] === "saved") {
            return { ...prev, [studentId]: "idle" };
          }
          return prev;
        });
      }, 2000);
    } catch (error) {
      console.error("Failed to update appreciation:", error);
      setSavingStates((prev) => ({ ...prev, [studentId]: "error" }));
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.target.blur();
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'OPEN': return 'Ouvert';
      case 'DRAFT': return 'Brouillon';
      case 'SUSPENDED': return 'Suspendu';
      case 'ARCHIVED': return 'Archivé';
      default: return status;
    }
  };

  const itemTemplate = (option) => {
    return (
      <div className="flex align-items-center">
        <span className="font-medium text-color">{option.name}</span>
        <span className={`text-xs px-2 py-1 border-round font-semibold ml-2 ${
          option.status === 'OPEN' ? 'bg-green-100 text-green-800' :
          option.status === 'DRAFT' ? 'bg-blue-100 text-blue-800' :
          option.status === 'SUSPENDED' ? 'bg-red-100 text-red-800' :
          'bg-gray-100 text-gray-800'
        }`}>
          {getStatusLabel(option.status)}
        </span>
      </div>
    );
  };

  const valueTemplate = (option, props) => {
    if (!option) {
      const currentReg = trainings.find(t => t.id === activeTrainingId) || training;
      if (!currentReg) {
        return <span>{props.placeholder}</span>;
      }
      option = currentReg;
    }
    return (
      <div className="flex align-items-center">
        <span className="font-bold text-color mr-2">{option.name}</span>
        <span className={`text-xs px-2 py-0.5 border-round font-semibold ${
          option.status === 'OPEN' ? 'bg-green-100 text-green-800' :
          option.status === 'DRAFT' ? 'bg-blue-100 text-blue-800' :
          option.status === 'SUSPENDED' ? 'bg-red-100 text-red-800' :
          'bg-gray-100 text-gray-800'
        }`}>
          {getStatusLabel(option.status)}
        </span>
      </div>
    );
  };

  const handleTrainingChange = async (e) => {
    const newTrainingId = e.value;
    if (newTrainingId && newTrainingId !== trainingId) {
      setPendingTrainingId(newTrainingId);
      try {
        const updatedPrefs = await accountsAPI.UpdateMemberPreferences(auth?.access_token, currentMembership?.id, newTrainingId);
        if (updatedPrefs) {
          updateCurrentMembershipPreferences(updatedPrefs);
        } else {
          const fallbackPrefs = currentMembership?.preferences 
            ? { ...currentMembership.preferences, last_visited_training: newTrainingId }
            : { last_visited_training: newTrainingId };
          updateCurrentMembershipPreferences(fallbackPrefs);
        }
      } catch (err) {
        console.error("Failed to update last visited preference", err);
      }
      navigate(`/school/${schoolId}/${basePath}/avis-passage/${newTrainingId}`);
    }
  };

  useEffect(() => {
    if (pendingTrainingId && pendingTrainingId === trainingId) {
      setPendingTrainingId(null);
    }
  }, [pendingTrainingId, trainingId]);

  const sectionOptions = isAdmin ? [
    { label: "Inscriptions", value: "candidates" },
    { label: "Paiements", value: "payments" },
    { label: "Campagnes d'e-mails", value: "campaigns" },
    { label: "Gestion de l'espace famille", value: "settings" },
    { label: "Avis de passage", value: "avis-passage" },
    { label: "Suivi des observations", value: "student-observations" },
  ] : [
    { label: "Mes séances de cours", value: "sessions" },
    { label: "Mes classes", value: "classes" },
    { label: "Avis de passage", value: "avis-passage" },
    { label: "Agenda", value: "agenda" },
  ];

  const handleSectionChange = (e) => {
    const newSection = e.value;
    if (!newSection || newSection === "avis-passage") {
      return;
    }

    if (isAdmin) {
      if (newSection === "student-observations") {
        navigate(`/school/${schoolId}/admin/student-observations/${trainingId}`);
        return;
      }
      navigate(`/school/${schoolId}/admin/registrations/${trainingId}/${newSection}`);
      return;
    }

    if (newSection === "sessions") {
      navigate(`/school/${schoolId}/teacher/sessions/${activeTrainingId}`);
      return;
    }

    if (newSection === "classes") {
      navigate(`/school/${schoolId}/teacher/classes/${activeTrainingId}`);
      return;
    }

    if (newSection === "agenda") {
      navigate(`/school/${schoolId}/teacher/agenda/${activeTrainingId}`);
    }
  };

  const customWorkspaceBar = (
    <div className="w-full p-breadcrumb border-bottom-1 surface-border py-2 px-3 flex align-items-center flex-wrap gap-2">
      <div className="flex align-items-center gap-2">
        <i className="pi pi-th-large text-teal-600 text-sm"></i>
        <Dropdown
          value="avis-passage"
          options={sectionOptions}
          onChange={handleSectionChange}
          className="dropdown-breadcrumb font-bold text-sm sm:text-base"
          style={{ width: 'auto' }}
        />
      </div>
      <span className="text-300 font-light text-base sm:text-lg">/</span>
      <div className="flex align-items-center gap-2">
        <i className="pi pi-bookmark text-indigo-500 text-sm"></i>
        <Dropdown
          value={activeTrainingId}
          options={trainings}
          optionLabel="name"
          optionValue="id"
          onChange={handleTrainingChange}
          placeholder={training?.name || "Sélectionner une formation"}
          className="dropdown-breadcrumb font-bold text-sm sm:text-base"
          style={{ width: 'auto' }}
          itemTemplate={itemTemplate}
          valueTemplate={valueTemplate}
        />
      </div>
      <span className="text-300 font-light text-base sm:text-lg">/</span>
      <span className="text-900 font-semibold text-sm sm:text-base px-2">
        {pendingTrainingId ? "Chargement..." : (classe?.name || "Classe")}
      </span>
    </div>
  );

  // Table columns templates
  const nameBodyTemplate = (rowData) => (
    <span className="font-semibold text-900">
      {rowData.lastname?.toUpperCase()} {rowData.firstname}
    </span>
  );

  const genderBodyTemplate = (rowData) => {
    const gender = rowData.gender;
    if (gender === "G") {
      return <span className="bg-pink-50 text-pink-700 px-2 py-0.5 border-round text-xs font-semibold">Fille</span>;
    }
    if (gender === "B") {
      return <span className="bg-blue-50 text-blue-700 px-2 py-0.5 border-round text-xs font-semibold">Garçon</span>;
    }
    return <span className="text-500">-</span>;
  };

  const birthdayBodyTemplate = (rowData) => rowData.birthday || "-";

  const absencesBodyTemplate = (rowData) => {
    const count = rowData.absences_count || 0;
    return (
      <span className={count > 0 ? "text-red-600 font-bold" : "text-600"}>
        {count}
      </span>
    );
  };

  const retardsBodyTemplate = (rowData) => {
    const count = rowData.retards_count || 0;
    return (
      <span className={count > 0 ? "text-orange-600 font-bold" : "text-600"}>
        {count}
      </span>
    );
  };

  const resultEditorTemplate = (rowData) => (
    <Dropdown
      value={rowData.result || "ONGOING"}
      options={resultOptions}
      onChange={(e) => handleResultChange(rowData.id, e.value)}
      valueTemplate={resultValueTemplate}
      itemTemplate={resultOptionTemplate}
      className="w-full p-inputtext-sm"
      placeholder="Avis de passage"
    />
  );

  const appreciationEditorTemplate = (rowData) => (
    <InputTextarea
      value={rowData.result_observation || ""}
      onChange={(e) => handleAppreciationChangeLocal(rowData.id, e.target.value)}
      onBlur={(e) => handleAppreciationSave(rowData.id, e.target.value)}
      onKeyDown={handleKeyDown}
      className="w-full p-inputtext-sm"
      placeholder="Saisir une appréciation..."
      rows={2}
      autoResize
    />
  );

  const saveStatusBodyTemplate = (rowData) => {
    const status = savingStates[rowData.id] || "idle";
    if (status === "saving") {
      return <i className="pi pi-spin pi-spinner text-teal-600 text-lg" title="Enregistrement en cours..."></i>;
    }
    if (status === "error") {
      return <i className="pi pi-exclamation-circle text-red-500 text-lg" title="Erreur de sauvegarde"></i>;
    }

    const isOngoing = !rowData.result || rowData.result === "ONGOING";
    if (isOngoing) {
      return <i className="pi pi-exclamation-triangle text-orange-500 text-lg" title="Veuillez sélectionner un avis de passage (Passage ou Redoublement)"></i>;
    }

    const hasAppreciation = rowData.result_observation && rowData.result_observation.trim() !== "";
    const isNotOngoing = rowData.result && rowData.result !== "ONGOING";

    if (isNotOngoing && hasAppreciation) {
      if (status === "saved") {
        return <i className="pi pi-check-circle text-green-500 text-lg" title="Enregistré avec succès !"></i>;
      }
      return <i className="pi pi-check text-green-500 font-bold text-sm" title="Synchronisé"></i>;
    }

    return null;
  };

  return (
    <MeaPage
      showBackButton={true}
      onBackButtonClick={() => navigate(`/school/${schoolId}/${basePath}/avis-passage/${trainingId}`)}
      title={pendingTrainingId ? "Chargement..." : (classe?.name || "Chargement...")}
      breadcrumb={pendingTrainingId ? undefined : customWorkspaceBar}
      customActions={
        <div className="flex align-items-center gap-2 mr-3">
          {hasUnsavedChanges ? (
            <>
              <i className="pi pi-spin pi-spinner text-teal-600 text-lg" title="Données non synchronisées"></i>
              <span className="text-teal-600 text-sm font-semibold hidden md:inline">Synchronisation en cours...</span>
            </>
          ) : (
            <>
              <i className="pi pi-check-circle text-green-500 text-lg" title="Données synchronisées"></i>
              <span className="text-green-500 text-sm font-semibold hidden md:inline">Synchronisé</span>
            </>
          )}
        </div>
      }
    >
      <div className="grid grid-nogutter p-0 md:pt-3 md:px-6 lg:px-8">
        {/* Class details card */}
        <div className="col-12 p-0 mb-3 md:p-3 md:mb-0">
          {classeQuery.isLoading ? (
            <Card className="shadow-1">
              <Skeleton width="100%" height="100px" />
            </Card>
          ) : isMobile ? (
            <div className="surface-card p-3 border-round shadow-1 border-1 border-100">
              <div className="text-xl font-bold text-teal-600 mb-2">{classe?.name || "Classe"}</div>
              <div className="text-sm text-700 mb-1">
                <strong>Niveau :</strong> {classe?.level?.name || "-"}
              </div>
              <div className="text-sm text-700 mb-1">
                <strong>Référent(e) :</strong>{" "}
                {
                  classe?.referent
                    ? (classe.referent.name
                      ? classe.referent.name
                      : `${classe.referent.lastname?.toUpperCase() || ""} ${classe.referent.firstname || ""}`.trim())
                    : "-"
                }
              </div>
              <div className="text-sm text-700">
                <strong>Cours / Créneaux :</strong> {formattedSchedule}
              </div>
            </div>
          ) : (
            <MeaDetailCard>
              <MeaDetailField field="Classe" value={classe?.name} />
              <MeaDetailField field="Niveau" value={classe?.level?.name || "-"} />
              <MeaDetailField
                field="Référent(e)"
                value={
                  classe?.referent
                    ? (classe.referent.name
                      ? classe.referent.name
                      : `${classe.referent.lastname?.toUpperCase() || ""} ${classe.referent.firstname || ""}`.trim())
                    : "-"
                }
              />
              <MeaDetailField field="Cours / Créneaux" value={formattedSchedule} />
            </MeaDetailCard>
          )}
        </div>

        {/* Students list/datatable card */}
        <div className="col-12 p-0 md:p-3">
          <div className="md:surface-card md:shadow-1 border-round p-1 md:p-4 border-none md:border-1 md:border-100">
            <h3 className="text-900 font-bold mb-3 md:mb-4 flex align-items-center gap-2 px-2 md:px-0">
              <i className="pi pi-users text-teal-600 text-xl"></i> Élèves inscrits
            </h3>

            {studentsQuery.isLoading ? (
              <div className="flex flex-column gap-2 p-2">
                <Skeleton width="100%" height="40px" />
                <Skeleton width="100%" height="40px" />
                <Skeleton width="100%" height="40px" />
              </div>
            ) : isMobile ? (
              <div className="flex flex-column gap-2">
                {students.length === 0 ? (
                  <div className="text-center py-4 text-500 text-sm">
                    Aucun élève inscrit dans cette classe.
                  </div>
                ) : (
                  students.map((student) => {
                    const result = student.result || "ONGOING";
                    let bgClass = "surface-card";
                    let borderClass = "border-100";
                    if (result === "PASSED") {
                      bgClass = "bg-green-50";
                      borderClass = "border-green-200";
                    } else if (result === "FAILED") {
                      bgClass = "bg-red-50";
                      borderClass = "border-red-200";
                    }
                    return (
                      <div
                        key={student.id}
                        className={`${bgClass} p-3 mb-2 border-round shadow-1 border-1 ${borderClass} flex flex-column gap-3`}
                      >
                        {/* Student Header */}
                        <div className="flex align-items-center justify-content-between pb-2 border-bottom-1 border-100">
                          <span className="font-semibold text-900">
                            {student.lastname?.toUpperCase()} {student.firstname}
                          </span>
                          <div className="flex align-items-center gap-2">
                            {genderBodyTemplate(student)}
                            {saveStatusBodyTemplate(student)}
                          </div>
                        </div>

                        {/* Birthdate & Attendance Info */}
                        <div className="grid grid-nogutter text-xs text-700 py-1">
                          <div className="col-6 mb-1">
                            <strong>Né(e) le :</strong> {student.birthday || "-"}
                          </div>
                          <div className="col-6 mb-1 text-right">
                            <strong>Absences :</strong>{" "}
                            <span className={student.absences_count > 0 ? "text-red-600 font-bold" : ""}>
                              {student.absences_count || 0}
                            </span>{" "}
                            | <strong>Retards :</strong>{" "}
                            <span className={student.retards_count > 0 ? "text-orange-600 font-bold" : ""}>
                              {student.retards_count || 0}
                            </span>
                          </div>
                        </div>

                        {/* Orientation Dropdown */}
                        <div className="flex flex-column gap-1">
                          <span className="text-600 text-xs font-semibold">Avis de passage :</span>
                          <Dropdown
                            value={student.result || "ONGOING"}
                            options={resultOptions}
                            onChange={(e) => handleResultChange(student.id, e.value)}
                            valueTemplate={resultValueTemplate}
                            itemTemplate={resultOptionTemplate}
                            className="w-full p-inputtext-sm"
                            placeholder="Avis de passage"
                          />
                        </div>

                        {/* Appreciation InputTextarea */}
                        <div className="flex flex-column gap-1">
                          <span className="text-600 text-xs font-semibold">Appréciation générale :</span>
                          <InputTextarea
                            value={student.result_observation || ""}
                            onChange={(e) => handleAppreciationChangeLocal(student.id, e.target.value)}
                            onBlur={(e) => handleAppreciationSave(student.id, e.target.value)}
                            onKeyDown={handleKeyDown}
                            className="w-full p-inputtext-sm"
                            placeholder="Saisir une appréciation..."
                            rows={2}
                            autoResize
                          />
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            ) : (
              <DataTable
                value={students}
                className="p-datatable-sm"
                emptyMessage="Aucun élève inscrit dans cette classe."
                responsiveLayout="scroll"
                rowClassName={(rowData) => {
                  const result = rowData.result || "ONGOING";
                  if (result === "PASSED") return "bg-green-50";
                  if (result === "FAILED") return "bg-red-50";
                  return "";
                }}
              >
                <Column header="Nom & Prénom" body={nameBodyTemplate} style={{ width: "15rem" }} />
                <Column header="Absences" body={absencesBodyTemplate} style={{ width: "6rem", textAlign: "center" }} />
                <Column header="Retards" body={retardsBodyTemplate} style={{ width: "6rem", textAlign: "center" }} />
                <Column header="Avis de passage" body={resultEditorTemplate} style={{ width: "12rem" }} />
                <Column header="Appréciation générale" body={appreciationEditorTemplate} style={{ width: "100%" }} />
                <Column body={saveStatusBodyTemplate} style={{ width: "4rem", textAlign: "center" }} />
              </DataTable>
            )}
          </div>
        </div>

        {/* Stats section at the bottom */}
        {!classeQuery.isLoading && students.length > 0 && (
          <div className="col-12 p-0 md:p-3 mt-3">
            <div className="surface-card p-4 border-round shadow-1 border-1 border-100">
              <h4 className="text-900 font-bold mb-3 mt-0 flex align-items-center gap-2">
                <i className="pi pi-chart-bar text-teal-600"></i> Répartition des avis
              </h4>
              <div className="flex flex-column md:flex-row md:align-items-center gap-3 md:gap-5 mb-3">
                <div className="flex align-items-center justify-content-between md:justify-content-start text-sm gap-2">
                  <span className="text-600 flex align-items-center gap-2">
                    <span className="w-1rem h-1rem bg-green-500 border-round"></span>
                    Passage :
                  </span>
                  <span className="font-semibold text-900">{passedCount} ({passedPercent}%)</span>
                </div>
                <div className="flex align-items-center justify-content-between md:justify-content-start text-sm gap-2">
                  <span className="text-600 flex align-items-center gap-2">
                    <span className="w-1rem h-1rem bg-red-500 border-round"></span>
                    Redoublement :
                  </span>
                  <span className="font-semibold text-900">{failedCount} ({failedPercent}%)</span>
                </div>
                <div className="flex align-items-center justify-content-between md:justify-content-start text-sm gap-2">
                  <span className="text-600 flex align-items-center gap-2">
                    <span className="w-1rem h-1rem bg-gray-400 border-round"></span>
                    Non décidés :
                  </span>
                  <span className="font-semibold text-900">{ongoingCount} ({ongoingPercent}%)</span>
                </div>
              </div>

              <div className="flex h-0.5rem border-round overflow-hidden bg-gray-200">
                {passedCount > 0 && (
                  <div 
                    className="bg-green-500 h-full" 
                    style={{ width: `${(passedCount / totalStudents) * 100}%` }}
                    title={`${passedPercent}% Passage`}
                  />
                )}
                {failedCount > 0 && (
                  <div 
                    className="bg-red-500 h-full" 
                    style={{ width: `${(failedCount / totalStudents) * 100}%` }}
                    title={`${failedPercent}% Redoublement`}
                  />
                )}
                {ongoingCount > 0 && (
                  <div 
                    className="bg-gray-400 h-full" 
                    style={{ width: `${(ongoingCount / totalStudents) * 100}%` }}
                    title={`${ongoingPercent}% Non décidé`}
                  />
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </MeaPage>
  );
}
